import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createCortexSystemPrompt, CortexPromptScenarioContext, CortexStage } from '../lib/cortexPrompt';
import { extractContactInfo, saveChatLead } from '../lib/saveChatLead';
import { generateClaudeResponse } from '../lib/claude';

export type Stage = CortexStage;

export interface CortexInsights {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  industry?: string | null;
  teamSize?: string | null;
  budget?: string | null;
  timeline?: string | null;
  stack?: string | null;
  painPoints?: string | null;
  kpi?: string | null;
  plan?: 'foundation' | 'launch' | 'vector' | null;
  companyAddress?: string | null;
  companyCity?: string | null;
  companyZip?: string | null;
  website?: string | null;
  notes?: string | null;
}

export interface CortexChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  stage?: Stage;
}

export type LeadStatus = 'idle' | 'saving' | 'success' | 'error';

const createId = () => crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const introCopy: Record<'it' | 'en', string> = {
  it: `Ciao, sono Cortex Analyst. Ti aiuto a capire se MANAGED CLOUD, AUTOMATION SUITE o SYSTEM INTEGRATION è la scelta corretta. Dimmi chi sei, di cosa si occupa l'azienda e quanti contatti gestisci al mese.\n- Da quali canali arrivano le richieste e con quali strumenti (email, WhatsApp, CRM) rispondete?\n- Qual è il problema principale oggi (tempi di risposta, lead persi, mancanza di automazioni)?\n- Budget indicativo e quando vuoi essere operativo?\nRaccolgo i dati e ti dico se bastano le fondamenta Managed Cloud, serve l'automazione completa o un'integrazione di sistema.`,
  en: `Hi, I'm Cortex Analyst. I help you choose between MANAGED CLOUD, AUTOMATION SUITE or SYSTEM INTEGRATION. Tell me who you are, what the company does and how many contacts you handle per month.\n- Which channels bring the requests and which tools (email, WhatsApp, CRM) do you rely on?\n- What's the most urgent bottleneck today (slow replies, lost leads, manual work)?\n- Indicative budget and target go-live?\nI'll gather everything and show whether Managed Cloud, Automation Suite, or a full System Integration is required.`,
};

const quickPromptSeeds: Record<'it' | 'en', string[]> = {
  it: [
    'Quanti contatti ricevi ogni mese?',
    'Hai bisogno di automazione WhatsApp o basta il sito?',
    'Che strumenti usi oggi (CRM, fogli, WhatsApp)?',
    'Quando vuoi essere online con il nuovo sistema?',
    'Dove operate (città, regione)?',
  ],
  en: [
    'How many contacts reach you per month?',
    'Do you need WhatsApp automation or is the website enough?',
    'Which tools do you use today (CRM, spreadsheets, WhatsApp)?',
    'When do you want the new system live?',
    'Where do you operate (city, region)?',
  ],
};

const PLAN_LABELS: Record<'foundation' | 'launch' | 'vector', { it: string; en: string }> = {
  foundation: { it: 'Managed Cloud', en: 'Managed Cloud' },
  launch: { it: 'Automation Suite', en: 'Automation Suite' },
  vector: { it: 'System Integration', en: 'System Integration' },
};

const PLAN_KEYWORD_PATTERNS: Record<'foundation' | 'launch' | 'vector', RegExp[]> = {
  foundation: [/\bmanaged\s+cloud\b/i, /\bfoundation\b/i],
  launch: [/\bautomation\s+suite\b/i, /\blaunch\b/i],
  vector: [/\bsystem\s+integration\b/i, /\bvector\b/i],
};

const detectPlanCodeInText = (text?: string | null): CortexInsights['plan'] | undefined => {
  if (!text) return undefined;
  for (const [code, patterns] of Object.entries(PLAN_KEYWORD_PATTERNS) as [
    'foundation' | 'launch' | 'vector',
    RegExp[],
  ][]) {
    if (patterns.some((pattern) => pattern.test(text))) {
      return code;
    }
  }
  return undefined;
};

const tierScenario = (language: 'it' | 'en'): CortexPromptScenarioContext => ({
  id: 'vector-tiers',
  title: language === 'it' ? 'Valutazione piani Vector' : 'Vector plan assessment',
  workflow: language === 'it' ? 'Qualifica → Piano consigliato' : 'Qualification → Suggested plan',
  industry: language === 'it' ? 'PMI, professionisti, scale-up' : 'SMBs, professional services, scale-ups',
  summary:
    language === 'it'
      ? 'Managed Cloud = infrastruttura e sicurezza, Automation Suite = automazione lead 24/7, System Integration = sviluppo custom e AI.'
      : 'Managed Cloud = infrastructure & security, Automation Suite = 24/7 lead automation, System Integration = custom build + AI.',
  primaryKpi: language === 'it' ? 'Tempo risposta e lead salvati' : 'Response time & captured leads',
  checkpoints: [
    language === 'it' ? 'Nome e azienda' : 'Name & company',
    language === 'it' ? 'Volumi contatti' : 'Contact volume',
    language === 'it' ? 'Canali attuali' : 'Current channels',
    language === 'it' ? 'Budget e timeline' : 'Budget & timeline',
    language === 'it' ? 'Stack digitale' : 'Digital stack',
  ],
});

interface ParsedAssistantPayload {
  text: string;
  stage?: Stage;
  data?: Partial<CortexInsights>;
}

const parseAssistantPayload = (raw: string): ParsedAssistantPayload => {
  if (!raw) return { text: '' };

  const stageMatch = raw.match(/\[STAGE:(analysis|vision|sales)\]/i);
  const stage = stageMatch ? (stageMatch[1].toLowerCase() as Stage) : undefined;

  const dataMatch = raw.match(/\[\[DATA\]\]([\s\S]+)$/i);
  let data: Partial<CortexInsights> | undefined;
  if (dataMatch) {
    try {
      data = JSON.parse(dataMatch[1].trim());
    } catch (err) {
      console.warn('[useCortexAnalyst] Failed to parse data block', err);
    }
  }

  const text = raw
    .replace(stageMatch?.[0] || '', '')
    .replace(dataMatch?.[0] || '', '')
    .trim();

  return { text, stage, data };
};

const sanitize = (value?: string | null) =>
  value ? value.replace(/\*\*/g, '').replace(/[\u200B]/g, '').trim() : value || undefined;

const mergeInsights = (current: CortexInsights, next?: Partial<CortexInsights>): CortexInsights => {
  if (!next) return current;
  const merged: CortexInsights = { ...current };
  Object.entries(next).forEach(([key, value]) => {
    if (value && typeof value === 'string' && value.trim().length > 0) {
      // @ts-expect-error dynamic assign
      merged[key] = sanitize(value);
    }
  });
  return merged;
};

const inferInsightsFromText = (
  text: string,
  contact: { full_name?: string | null; email?: string | null; company?: string | null },
): Partial<CortexInsights> => {
  const normalized = text || '';
  const lowered = normalized.toLowerCase();

  const matchValue = (patterns: RegExp[]) => {
    for (const pattern of patterns) {
      const match = normalized.match(pattern);
      if (match && match[1]) {
        return match[1].trim();
      }
    }
    return undefined;
  };

  const result: Partial<CortexInsights> = {};
  if (contact.full_name) result.name = contact.full_name;
  if (contact.email) result.email = contact.email;
  if (contact.company) result.company = contact.company;

  // Improved NLP-like extraction for instant feedback
  const nameText =
    matchValue([/Nome[:\-\s]+([^\n]+)/i, /Name[:\-\s]+([^\n]+)/i]) ||
    matchValue([/(?:mi chiamo|sono|name is|i am)\s+([A-Z][a-zÀ-ÿ]+(?:\s+[A-Z][a-zÀ-ÿ]+)*)/]);
  if (nameText) result.name = nameText;

  const companyText =
    matchValue([/Azienda[:\-\s]+([^\n]+)/i, /Company[:\-\s]+([^\n]+)/i]) ||
    matchValue([/(?:azienda|società|company|firm)\s+(?:si chiama|è|is)\s+([^\n.,]+)/i]);
  if (companyText) result.company = companyText;

  const emailText = matchValue([/Email[:\-\s]+([^\n]+)/i]);
  if (emailText) result.email = emailText;

  const industryText = matchValue([/Settore[:\-\s]+([^\n]+)/i, /Industry[:\-\s]+([^\n]+)/i]);
  if (industryText) result.industry = industryText;

  const budgetText =
    matchValue([/Budget[^\n]*[:\-\s]+([^\n]+)/i, /Investimento[:\-\s]+([^\n]+)/i]) ||
    matchValue([/\b(?:budget|investimento|spesa)\b[^€$0-9]*([€$]?(?:\d{1,3}(?:[.,]\d{3})*|\d+)(?:[.,]\d{2})?)/i]);
  if (budgetText) result.budget = budgetText;

  const timelineText =
    matchValue([/Partenza[:\-\s]+([^\n]+)/i, /Timeline[:\-\s]+([^\n]+)/i, /Go-?live[:\-\s]+([^\n]+)/i]) ||
    matchValue([/\b(live|operativ[oa])\b[^\n]*[:\-\s]+([^\n]+)/i]);
  if (timelineText) result.timeline = timelineText;

  const stackText = matchValue([/Strumenti[:\-\s]+([^\n]+)/i, /Stack[:\-\s]+([^\n]+)/i, /Tools[:\-\s]+([^\n]+)/i]);
  if (stackText) result.stack = stackText;

  const painText = matchValue([/Problema[:\-\s]+([^\n]+)/i, /Pain[:\-\s]+([^\n]+)/i, /Bottleneck[:\-\s]+([^\n]+)/i]);
  if (painText) result.painPoints = painText;

  const addressText =
    matchValue([/Indirizzo[:\-\s]+([^\n]+)/i, /Address[:\-\s]+([^\n]+)/i]) ||
    matchValue([/(?:sede|studio|filiale)\s+(?:a|in)\s+([A-Za-zÀ-ÿ\s]+)/i]);
  if (addressText) result.companyAddress = addressText;

  const cityText =
    matchValue([/Citt[aà][:–\s]+([^\n]+)/i, /City[:\-\s]+([^\n]+)/i, /Comune[:\-\s]+([^\n]+)/i]) ||
    matchValue([/(?:in|a)\s+([A-Z][A-Za-zÀ-ÿ\s]+)\s+(?:\b(?:city|citt[aà]|provincia)\b)/i]);
  if (cityText) result.companyCity = cityText;

  const zipText = matchValue([/CAP[:\-\s]+([0-9]{4,6})/i, /ZIP[:\-\s]+([0-9]{4,6})/i]);
  if (zipText) result.companyZip = zipText;

  const websiteMatch = normalized.match(/\bhttps?:\/\/[^\s)]+/i);
  if (websiteMatch) result.website = websiteMatch[0];

  const kpiText = matchValue([/KPI[:\-\s]+([^\n]+)/i]);
  if (kpiText) result.kpi = kpiText;

  const planText = matchValue([
    /Piano[:\-\s]+([^\n]+)/i,
    /Plan[:\-\s]+([^\n]+)/i,
  ]);
  const inferredPlan = detectPlanCodeInText(planText);
  if (inferredPlan) {
    result.plan = inferredPlan;
  }

  if (!result.plan) {
    const planFromBody = detectPlanCodeInText(normalized);
    if (planFromBody) {
      result.plan = planFromBody;
    }
  }

  const channels: string[] = [];
  if (/\bwhats?\s*app\b/i.test(lowered)) channels.push('WhatsApp');
  if (/\bfacebook\b/i.test(lowered)) channels.push('Facebook');
  if (/\binstagram\b/i.test(lowered)) channels.push('Instagram');
  if (/\bemail\b/i.test(lowered)) channels.push('Email');
  if (channels.length && !result.stack) {
    result.stack = channels.join(', ');
  }

  if (!result.painPoints && /lead\b.*pers[ia]/i.test(lowered)) {
    result.painPoints = 'Lead persi o non gestiti';
  } else if (!result.painPoints && /mancanza.*automaz/i.test(lowered)) {
    result.painPoints = 'Mancano automazioni';
  }

  return result;
};

export function useCortexAnalyst() {
  const { i18n } = useTranslation();
  const language: 'it' | 'en' = i18n.language.startsWith('it') ? 'it' : 'en';
  const scenarioContext = useMemo(() => tierScenario(language), [language]);
  const primerTokenRef = useRef<string>('');

  // Important: use useState for everything that triggers re-renders, but be careful with refs
  const [messages, setMessages] = useState<CortexChatMessage[]>([]);
  const [stage, setStage] = useState<Stage>('analysis');
  const [insights, setInsights] = useState<CortexInsights>({});
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leadStatus, setLeadStatus] = useState<LeadStatus>('idle');
  const [leadError, setLeadError] = useState<string | null>(null);

  // Refs for things that don't need re-renders
  const conversationRef = useRef<{ role: 'system' | 'user' | 'assistant'; content: string }[]>([]);
  const leadIdentifierRef = useRef<{ email?: string | null; phone?: string | null }>({});
  const pendingLeadKeyRef = useRef<string | null>(null);

  // Initialization logic
  useEffect(() => {
    const token = `${language}-primer`;
    if (primerTokenRef.current === token) return;
    
    primerTokenRef.current = token;
    
    const intro = introCopy[language];
    conversationRef.current = [
      {
        role: 'system',
        content: createCortexSystemPrompt({ language, scenario: scenarioContext }),
      },
      {
        role: 'assistant',
        content: intro,
      },
    ];
    setMessages([{ id: createId(), role: 'assistant', content: intro, stage: 'analysis' }]);
    setStage('analysis');
    setInsights({});
    setError(null);
    setLeadStatus('idle');
    setLeadError(null);
    leadIdentifierRef.current = {};
  }, [language, scenarioContext]);

  const maybeSaveLead = useCallback(
    async (history: { role: string; content: string }[], currentInsights: CortexInsights) => {
      const extracted = extractContactInfo(history);
      const email = currentInsights.email || extracted.email;
      const phone = extracted.phone;
      
      // Simple email validation: must have @ and . and be reasonably short
      const isValidEmail = email && email.includes('@') && email.includes('.') && email.length < 100;
      if (!isValidEmail && !phone) return;

      // Don't save if the "email" is actually a sentence or description
      if (email && !isValidEmail) {
        console.warn('[useCortexAnalyst] Ignored invalid email candidate:', email);
        if (!phone) return;
      }

      const finalEmail = isValidEmail ? email : null;
      const leadKey = finalEmail ?? phone ?? null;

      const alreadySaved =
        (finalEmail && leadIdentifierRef.current.email === finalEmail) ||
        (phone && leadIdentifierRef.current.phone === phone) ||
        (leadKey && pendingLeadKeyRef.current === leadKey);
      if (alreadySaved) return;

      try {
        if (leadKey) {
          pendingLeadKeyRef.current = leadKey;
        }
        setLeadStatus('saving');
        setLeadError(null);
        
        const result = await saveChatLead({
          full_name: currentInsights.name || extracted.full_name || null,
          email: finalEmail || null,
          phone: phone || null,
          company: currentInsights.company || extracted.company || null,
          source: 'cortex-analyst',
          language,
          skipEmail: true, // background auto-save, no email
          companyAddress: currentInsights.companyAddress,
          companyCity: currentInsights.companyCity,
          companyZip: currentInsights.companyZip,
          website: currentInsights.website,
        });
        if (result.success) {
          leadIdentifierRef.current = { email: finalEmail || null, phone: phone || null };
          setLeadStatus('success');
        } else {
          setLeadStatus('error');
          setLeadError(result.error || 'Errore salvataggio lead');
        }
      } catch (err: any) {
        setLeadStatus('error');
        setLeadError(err?.message || 'Errore salvataggio lead');
      } finally {
        if (leadKey && pendingLeadKeyRef.current === leadKey) {
          pendingLeadKeyRef.current = null;
        }
      }
    },
    [language],
  );

  // New function to explicitly trigger the "closed won" email / appointment
  const closeDeal = useCallback(async (appointmentDate?: string) => {
    const { email, phone, name, company } = insights;
    if (!email && !phone) return;

    try {
      setLeadStatus('saving');
      await saveChatLead({
        full_name: name,
        email,
        phone,
        company,
        source: 'cortex-analyst',
        language,
        skipEmail: false,
        forceEmail: true,
        companyAddress: insights.companyAddress,
        companyCity: insights.companyCity,
        companyZip: insights.companyZip,
        website: insights.website,
        createAppointment: !!appointmentDate,
        appointmentDetails: appointmentDate ? {
          date: appointmentDate,
          title: `Cortex Check-up: ${company || name}`,
          notes: `Plan interested: ${insights.plan || 'TBD'}. Budget: ${insights.budget}. Stack: ${insights.stack}`
        } : undefined
      });
      setLeadStatus('success');
    } catch (err) {
      console.error('Failed to close deal', err);
      setLeadStatus('error');
    }
  }, [insights, language]);

  const runClaude = useCallback(
    async (history: { role: 'system' | 'user' | 'assistant'; content: string }[]) => {
      setIsThinking(true);
      setError(null);
      try {
        const content = await generateClaudeResponse(history);
        const parsed = parseAssistantPayload(content);
        const assistantMsg = { role: 'assistant' as const, content: parsed.text };
        const updatedHistory = [...history, assistantMsg];
        conversationRef.current = updatedHistory;
        setMessages((prev) => [...prev, { id: createId(), role: 'assistant', content: parsed.text, stage: parsed.stage }]);
        if (parsed.stage) {
          setStage(parsed.stage);
        }
        const contactFallback = extractContactInfo(updatedHistory);
        const inferred = inferInsightsFromText(parsed.text, contactFallback);
        const combined = { ...inferred, ...(parsed.data || {}) };
        const isClosingAction =
          parsed.text.includes('[ACTION:CLOSE_DEAL]') ||
          parsed.text.includes('[ACTION:BOOK_MEETING]') ||
          parsed.stage === 'sales';
        setInsights((prev) => {
          const merged = mergeInsights(prev, combined);
          if (!isClosingAction) {
            maybeSaveLead(updatedHistory, merged);
          }
          return merged;
        });

        // NEW: Check if the AI decided to close the deal / book a meeting
        if (isClosingAction) {
          setStage((prev) => (prev === 'sales' ? prev : 'sales'));
          const dateMatch = parsed.text.match(/\[APPOINTMENT_TIME:([^\]]+)\]/);
          const date = dateMatch ? dateMatch[1] : undefined;
          closeDeal(date);
        }

      } catch (err: any) {
        console.error('[useCortexAnalyst] Claude error', err);
        setError(err?.message || 'Errore di connessione con Claude');
      } finally {
        setIsThinking(false);
      }
    },
    [maybeSaveLead, scenarioContext, closeDeal],
  );

  const deriveFromUserText = useCallback((text: string) => {
    const contactFallback = extractContactInfo([{ role: 'user', content: text }]);
    const inferred = inferInsightsFromText(text, contactFallback);
    if (Object.keys(inferred).length) {
      setInsights((prev) => mergeInsights(prev, inferred));
    }
  }, []);

  const sendMessage = useCallback(
    async (text: string, options?: { silent?: boolean }) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      deriveFromUserText(trimmed);
      const userMsg = { role: 'user' as const, content: trimmed };
      const updatedHistory = [...conversationRef.current, userMsg];
      conversationRef.current = updatedHistory;
      if (!options?.silent) {
        setMessages((prev) => [...prev, { id: createId(), role: 'user', content: trimmed }]);
      }
      await runClaude(updatedHistory);
    },
    [runClaude, deriveFromUserText],
  );

  const requestProposal = useCallback(async () => {
    const planLabel = insights.plan
      ? PLAN_LABELS[insights.plan][language]
      : language === 'it'
      ? 'piano migliore'
      : 'best-fit plan';

    const prompt =
      language === 'it'
        ? `In base ai dati raccolti, spiega perché il piano ${planLabel} è adatto, cosa consegniamo nelle prime 4 settimane e qual è il prossimo step (es. check-up gratuito).`
        : `Based on the collected data, explain why the ${planLabel} plan fits, what we deliver in the first 4 weeks, and the next step (e.g. free check-up).`;
    await sendMessage(prompt, { silent: false });
  }, [insights.plan, language, sendMessage]);

  const suggestions = useMemo(() => {
    const base = quickPromptSeeds[language] || [];
    const missing: string[] = [];
    if (!insights.budget) missing.push(language === 'it' ? 'Qual è il budget disponibile?' : 'What budget do you have?');
    if (!insights.timeline) missing.push(language === 'it' ? 'Quando vuoi andare live?' : 'When do you need to go live?');
    if (!insights.stack) missing.push(language === 'it' ? 'Che strumenti usi oggi?' : 'Which tools do you use today?');
    if (!insights.plan) missing.push(language === 'it' ? 'Posso suggerire il piano ideale?' : 'May I suggest the best plan?');
    return Array.from(new Set([...missing, ...base])).slice(0, 4);
  }, [insights, language]);

  return {
    messages,
    stage,
    insights,
    isThinking,
    error,
    leadStatus,
    leadError,
    suggestions,
    sendMessage,
    requestProposal,
  };
}
